import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, MessageSquare } from "lucide-react";
import { Wordmark } from "./Logo";
import { openEmail, openSMS } from "@/lib/contact";

const PHONE = "561-502-6769";
const JONAS_PHONE = "561-251-0539";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-[#0A0A0A] border-t border-[#1c1c1c]">
      {/* Big CTA slab */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 spotlight opacity-60 pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="flex items-baseline gap-4 mb-10"
          >
            <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#E31E24]">
              (08) · Ready?
            </span>
            <span className="flex-1 h-px bg-white/15 mt-1" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-anton uppercase leading-[0.88] text-[12vw] md:text-[8vw] lg:text-[7vw] mb-10 max-w-6xl"
          >
            If the car exists,<br />
            <span className="font-cormorant italic font-medium text-[#E31E24]">we'll find it.</span>
          </motion.h2>

          <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-8">
            <a
              href="#find"
              data-testid="footer-cta-find"
              className="group inline-flex items-center justify-between gap-6 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] px-6 py-4 transition-colors"
            >
              <span className="font-anton uppercase tracking-[0.14em] text-lg">Find My Car</span>
              <ArrowUpRight size={22} className="group-hover:rotate-45 transition-transform" />
            </a>
            <a
              href="#sell"
              data-testid="footer-cta-sell"
              className="group inline-flex items-center justify-between gap-6 border border-white/40 hover:border-white hover:bg-white/5 px-6 py-4 transition-colors"
            >
              <span className="font-anton uppercase tracking-[0.14em] text-lg">Sell or Trade</span>
              <ArrowUpRight size={22} className="group-hover:rotate-45 transition-transform" />
            </a>
            <a
              href={`tel:${PHONE}`}
              data-testid="footer-cta-call"
              className="inline-flex items-center gap-3 px-6 py-4 border border-[#E31E24] hover:bg-[#E31E24] font-mono2 uppercase tracking-[0.14em] text-sm"
            >
              <Phone size={16} /> {PHONE}
            </a>
          </div>

          <div className="flex flex-wrap gap-3 mb-14">
            <button
              type="button"
              data-testid="footer-email-btn"
              onClick={() => openEmail({ subject: "Hello Strive Motors" })}
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#E31E24] hover:text-[#E31E24] px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em] transition-colors"
            >
              <Mail size={14} /> Email Us
            </button>
            <button
              type="button"
              data-testid="footer-sms-btn"
              onClick={() => openSMS({ body: "Hi Strive Motors · " })}
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#E31E24] hover:text-[#E31E24] px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em] transition-colors"
            >
              <MessageSquare size={14} /> Text Us
            </button>
          </div>

          <div className="max-w-2xl">
            <p className="text-white/60 font-cormorant italic text-2xl leading-snug">
              "New customers we treat and call family are always welcome."
            </p>
          </div>
        </div>
      </div>

      {/* Bottom info bar */}
      <div className="border-t border-[#1c1c1c]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <Wordmark height={72} />
            <p className="text-white/50 text-sm mt-4 max-w-xs">
              Concierge vehicle sourcing. We search our network and market sources to help you find the vehicle you want.
            </p>
          </div>
          <div className="col-span-6 lg:col-span-2">
            <div className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-white/60 mb-3">Navigate</div>
            <ul className="space-y-1.5 text-white/80 text-sm">
              <li><a href="#how" className="link-sweep">Process</a></li>
              <li><a href="#find" className="link-sweep">Find My Car</a></li>
              <li><a href="#sell" className="link-sweep">Sell / Trade</a></li>
              <li><a href="#gallery" className="link-sweep">Recently Sourced</a></li>
              <li><a href="#faq" className="link-sweep">FAQ</a></li>
            </ul>
          </div>
          <div className="col-span-6 lg:col-span-3">
            <div className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-white/60 mb-3">Contact</div>
            <div className="space-y-2.5 text-white/80 text-sm">
              <div>
                <div className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-white/50">Montus Olivier · Partner</div>
                <a href={`tel:${PHONE}`} data-testid="footer-montus-tel" className="text-white hover:text-[#E31E24] font-mono2">
                  {PHONE}
                </a>
              </div>
              <div>
                <div className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-white/50">Jonas Destine · Partner</div>
                <a href={`tel:${JONAS_PHONE}`} data-testid="footer-jonas-tel" className="text-white hover:text-[#E31E24] font-mono2">
                  {JONAS_PHONE}
                </a>
              </div>
              <div className="pt-1">Boca Raton · Delray Beach, Florida</div>
              <div>Mon · Sat · By appointment</div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-3">
            <div className="border border-[#E31E24]/50 p-4">
              <div className="font-anton uppercase text-lg leading-tight">
                If something small · we fix it.
              </div>
              <div className="font-cormorant italic text-[#E31E24] text-2xl mt-1">
                28 days guaranteed.
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-[#1c1c1c]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono2 uppercase tracking-[0.28em] text-white/60">
            <div>© {new Date().getFullYear()} Strive Motors · Buy · Sell · Trade</div>
            <div className="text-[#E31E24]">Family Run · 15 Years Experience</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
